'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

interface RevenueEntry {
  id: string;
  source: string;
  type: 'campaign' | 'agency' | 'platform' | 'international';
  amount: number;
  currency: 'DZD' | 'EUR' | 'USD';
  date: string;
  invoiceStatus: 'issued' | 'pending' | 'received';
  dealId?: string;
  contract: boolean;
}

const revenues: RevenueEntry[] = [
  { id: '1', source: 'Djezzy', type: 'campaign', amount: 350000, currency: 'DZD', date: '12 Aoû 2026', invoiceStatus: 'received', dealId: 'DZ-2026-0001842', contract: true },
  { id: '2', source: 'Ooredoo Algérie', type: 'campaign', amount: 420000, currency: 'DZD', date: '28 Juil 2026', invoiceStatus: 'received', dealId: 'DZ-2026-0001756', contract: true },
  { id: '3', source: 'Cevital via Créa+', type: 'agency', amount: 280000, currency: 'DZD', date: '10 Juil 2026', invoiceStatus: 'received', dealId: 'DZ-2026-0001690', contract: true },
  { id: '4', source: 'Mobilis', type: 'campaign', amount: 190000, currency: 'DZD', date: '22 Juin 2026', invoiceStatus: 'received', dealId: 'DZ-2026-0001621', contract: true },
  { id: '5', source: 'Brand EU Partner', type: 'international', amount: 1200, currency: 'EUR', date: '15 Juin 2026', invoiceStatus: 'received', contract: true },
  { id: '6', source: 'YouTube AdSense', type: 'platform', amount: 3000, currency: 'DZD', date: '01 Aoû 2026', invoiceStatus: 'received', contract: false },
  { id: '7', source: 'Yassir', type: 'campaign', amount: 310000, currency: 'DZD', date: '18 Aoû 2026', invoiceStatus: 'pending', dealId: 'DZ-2026-0001901', contract: true },
  { id: '8', source: 'International Brand 2', type: 'international', amount: 3000, currency: 'EUR', date: '10 Aoû 2026', invoiceStatus: 'issued', contract: true },
];

const typeLabels: Record<RevenueEntry['type'], string> = {
  campaign: 'Campagne', agency: 'Agence', platform: 'Plateforme', international: 'International'
};
const invoiceLabels: Record<RevenueEntry['invoiceStatus'], { label: string; cls: string }> = {
  issued: { label: 'Émise', cls: 'text-info' },
  pending: { label: 'En attente', cls: 'text-warning' },
  received: { label: 'Reçue', cls: 'text-positive' },
};

export default function CreatorRevenuePage() {
  const [locked, setLocked] = useState(true);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const [exportReady, setExportReady] = useState(false);

  const totalDZD = revenues.filter(r => r.currency === 'DZD').reduce((a, r) => a + r.amount, 0);
  const totalEUR = revenues.filter(r => r.currency === 'EUR').reduce((a, r) => a + r.amount, 0);
  const contracts = revenues.filter(r => r.contract).length;
  const invoicesIssued = revenues.filter(r => r.invoiceStatus !== 'pending').length;
  const pending = revenues.filter(r => r.invoiceStatus === 'pending').length;
  const completeness = Math.round((contracts / revenues.length) * 100);

  const handleUnlock = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin === '1234') { setLocked(false); setPinError(false); }
    else { setPinError(true); setPin(''); }
  };

  if (locked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-2xl p-8 w-full max-w-sm shadow-2xl">
          <div className="flex flex-col items-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-3">
              <Icon name="LockClosedIcon" size={24} className="text-primary" />
            </div>
            <h1 className="text-lg font-700 text-foreground">Revenue Center</h1>
            <p className="text-xs text-muted-foreground mt-1 text-center">Espace privé créateur · Données financières confidentielles</p>
          </div>
          <form onSubmit={handleUnlock} className="space-y-4">
            <input type="password" value={pin} onChange={e => { setPin(e.target.value); setPinError(false); }} placeholder="Code PIN" maxLength={6} autoFocus className={`w-full bg-muted border rounded-xl px-4 py-3 text-center text-xl font-700 tracking-widest text-foreground outline-none transition-all ${pinError ? 'border-negative' : 'border-border focus:border-primary'}`} />
            {pinError && <p className="text-xs text-negative text-center font-600">Code incorrect</p>}
            <p className="text-[11px] text-muted-foreground text-center">Demo PIN: 1234</p>
            <button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-700 py-3 rounded-xl text-sm transition-all">Accéder</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <AppLayout currentPath="/creator-revenue">
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-700 uppercase tracking-widest text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full">Revenue Center · Privé</span>
            </div>
            <h1 className="text-2xl font-700 text-foreground">Revenus & Documentation</h1>
            <p className="text-sm text-muted-foreground mt-1">Registre privé · Données visibles uniquement par le créateur</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setExportReady(true); setTimeout(() => setExportReady(false), 2000); }} className="flex items-center gap-1.5 px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg text-sm font-600 transition-colors">
              <Icon name={exportReady ? 'CheckIcon' : 'ArrowDownTrayIcon'} size={14} />
              {exportReady ? 'Prêt !' : 'Tax Readiness Export'}
            </button>
          </div>
        </div>

        {/* Tax Readiness Disclaimer */}
        <div className="bg-warning/5 border border-warning/20 rounded-xl p-4 flex gap-3">
          <Icon name="ExclamationTriangleIcon" size={18} className="text-warning flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-600 text-foreground">ISX n'est pas une administration fiscale</p>
            <p className="text-xs text-muted-foreground mt-1">Ce centre de revenus est un outil d'organisation et de préparation documentaire. ISX ne certifie pas la conformité fiscale et ne calcule pas vos impôts. Le Tax Readiness Export est une préparation de données destinée à votre comptable — pas une déclaration fiscale officielle. Toute question fiscale doit être validée avec un expert-comptable agréé selon le droit algérien en vigueur.</p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
          {[
            { label: 'Revenus DZD', value: `${(totalDZD / 1000000).toFixed(2)}M`, sub: 'DZD', color: 'text-positive' },
            { label: 'Revenus EUR', value: `${totalEUR.toLocaleString()}`, sub: 'EUR', color: 'text-info' },
            { label: 'Contrats', value: `${contracts}/${revenues.length}`, sub: 'enregistrés', color: 'text-foreground' },
            { label: 'Factures émises', value: `${invoicesIssued}`, sub: 'sur ' + revenues.length, color: 'text-foreground' },
            { label: 'En attente', value: `${pending}`, sub: 'factures', color: 'text-warning' },
            { label: 'Complétude docs', value: `${completeness}%`, sub: 'Tax Ready', color: completeness >= 90 ? 'text-positive' : 'text-warning' },
          ].map(s => (
            <div key={s.label} className="bg-card border border-border rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-600 mb-1">{s.label}</p>
              <p className={`text-metric-md font-mono ${s.color}`}>{s.value}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{s.sub}</p>
            </div>
          ))}
        </div>

        {/* Revenue Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-700 text-foreground">Registre des revenus</h2>
            <span className="text-xs text-muted-foreground">{revenues.length} entrées</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Source</th>
                  <th className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Type</th>
                  <th className="text-right px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Montant</th>
                  <th className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Date</th>
                  <th className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Facture</th>
                  <th className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Contrat</th>
                  <th className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Deal ID</th>
                </tr>
              </thead>
              <tbody>
                {revenues.map((r, i) => (
                  <tr key={r.id} className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
                    <td className="px-4 py-3 text-sm font-600 text-foreground">{r.source}</td>
                    <td className="px-4 py-3"><span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">{typeLabels[r.type]}</span></td>
                    <td className="px-4 py-3 text-right font-mono text-sm font-600 text-foreground">{r.amount.toLocaleString()} {r.currency}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{r.date}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-600 ${invoiceLabels[r.invoiceStatus].cls}`}>{invoiceLabels[r.invoiceStatus].label}</span></td>
                    <td className="px-4 py-3">{r.contract ? <Icon name="CheckCircleIcon" size={14} className="text-positive" /> : <Icon name="XCircleIcon" size={14} className="text-muted-foreground" />}</td>
                    <td className="px-4 py-3"><span className="font-mono text-[11px] text-primary">{r.dealId || '—'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
